import { AppRoutes } from '@/routes/appRoutes';
import { useFormikContext } from 'formik';
import { TKeys, useTranslate } from '@/i18n/useTranslate';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nullable, PartialRecord } from '@/types';
import { useEditContact, useProfileData } from '@/store/profile/hooks';
import * as yup from 'yup';

import { FormikRedux, Grid } from '@/components';
import { WhiteBox } from '@/components/WhiteBox';
import { Stack } from '@mui/material';
import { DateTime } from 'luxon';
import {
  BirthSex,
  Contact,
  ContactEthnicityEnum,
  ContactGenderEnum,
  ContactRaceEnum,
  ContactSexualOrientationEnum,
} from '@/api/__generated__/webApi';
import { TranslationKeys } from '@/i18n/TranslationKeys';
import {
  BIRTH_SEX_ENUM_MAP,
  ETHNICITY_ENUM_MAP,
  GENDER_ENUM_MAP,
  RACE_ENUM_MAP,
  SEXUAL_ORIENTATION_ENUM_MAP,
} from '@/features/profile/ProfileEdit/enumMaps';
import { EMPTY } from '@/constants';
import { InputControlF, SelectControlF } from '@/components/FormikRedux';
import { Button } from 'ui-kit';
import { DateControlF } from '@/components/FormikRedux/DateControlF.tsx';
import { SelectOption } from '@/components/SelectControl';
import DATA_TEST_IDS from './dataTestIds.ts';
import { testId } from '@/utils/testId.ts';
type OptionKeys = Extract<
  keyof Contact,
  'birthSex' | 'ethnicity' | 'gender' | 'race' | 'sexualOrientation'
>;

const styles = {
  buttonsLayout: {
    justifyContent: { xs: 'initial', sm: 'space-between', md: 'initial' },
    button: { width: { xs: '100%', sm: 'auto' } },
    mt: { xs: 40, sm: 32 },
    gap: 24,
    flexDirection: 'row',
  },
};

const getTranslations = <T extends Record<string, string>>(
  enumeration: T,
  map: Record<T[keyof T], keyof TranslationKeys['common']>,
  t: (str: TKeys<'common'> | undefined | null) => string | undefined
): Record<T[keyof T], string> => {
  return Object.values(enumeration).reduce<Record<string, string>>(
    (acc, value) => {
      // @ts-expect-error legacy. some old code, can be left as is
      acc[value] = t(map[value]);
      return acc;
    },
    {}
  );
};
const useOptions = (): Record<
  OptionKeys,
  { value: string; label: string }[]
> => {
  const { t } = useTranslate('common');

  return useMemo<Record<OptionKeys, { label: string; value: string }[]>>(() => {
    const birthSex = getTranslations(BirthSex, BIRTH_SEX_ENUM_MAP, t);

    const ethnicity = getTranslations(
      ContactEthnicityEnum,
      ETHNICITY_ENUM_MAP,
      t
    );
    const gender = getTranslations(ContactGenderEnum, GENDER_ENUM_MAP, t);
    const race = getTranslations(ContactRaceEnum, RACE_ENUM_MAP, t);

    const sexualOrientation = getTranslations(
      ContactSexualOrientationEnum,
      SEXUAL_ORIENTATION_ENUM_MAP,
      t
    );

    return {
      birthSex: Object.entries(birthSex).map(([value, label]) => ({
        label,
        value,
      })),
      ethnicity: Object.entries(ethnicity).map(([value, label]) => ({
        label,
        value,
      })),
      gender: Object.entries(gender).map(([value, label]) => ({
        label,
        value,
      })),
      race: Object.entries(race).map(([value, label]) => ({ label, value })),
      sexualOrientation: Object.entries(sexualOrientation).map(
        ([value, label]) => ({ label, value })
      ),
    };
  }, [t]);
};

const useValidationSchema = () => {
  const { t } = useTranslate('profile');
  return useMemo(() => {
    return yup.object().shape({
      birthDate: yup
        .string()
        .nullable()
        .required(t('FIELD_IS_REQUIRED'))
        .test('is-18', t('TO_USE_THE_SERVICE_'), (date) => {
          const lDate = DateTime.fromISO(date ?? '');
          if (!lDate.isValid) {
            return true;
          }
          const age = DateTime.now().diff(lDate, 'years').years;
          return age >= 18;
        }),
      firstName: yup.string().nullable().required(t('FIELD_IS_REQUIRED')),
      lastName: yup.string().nullable().required(t('FIELD_IS_REQUIRED')),
      middleName: yup.string().nullable(),

      birthSex: yup.string().nullable().required(t('FIELD_IS_REQUIRED')),
      ethnicity: yup.string().nullable(),
      gender: yup.string().nullable().required(t('FIELD_IS_REQUIRED')),
      sexualOrientation: yup.string().nullable(),
      race: yup.string().nullable(),
    });
  }, [t]);
};

const initialContact: Nullable<Contact> = {
  birthDate: '',
  firstName: '',
  lastName: '',
  middleName: '',
  race: '' as ContactRaceEnum,
  ethnicity: null,
  birthSex: null,
  gender: null,
  sexualOrientation: null,
};

const getRequired = (schema: ReturnType<typeof useValidationSchema>) => {
  return Object.entries(schema.fields).reduce<
    PartialRecord<keyof Contact, { required: boolean }>
  >((acc, [key, value]) => {
    acc[key as keyof Contact] = {
      // todo: [tech-debt] Update YUP to latest version and use different approach to get required fields
      // eslint-disable-next-line
      required: (value as any).exclusiveTests.required ?? false,
    };
    return acc;
  }, {});
};

export const ProfileEdit: FC = () => {
  const { details } = useProfileData();
  const {
    sendData,
    validationErrors,
    resetServerErrors,
    success,
    fetching,
    resetForm,
  } = useEditContact();
  const navigate = useNavigate();
  const contact = details?.contact ?? initialContact;
  const schema = useValidationSchema();
  const submitForm = useCallback(
    (d: Nullable<Contact>) => {
      sendData(d);
    },
    [sendData]
  );

  useEffect(() => {
    if (!fetching && success) {
      navigate({ pathname: AppRoutes.PROFILE, search: window.location.search });
      resetForm();
    }
  }, [fetching, navigate, resetForm, success]);

  return (
    <FormikRedux<Nullable<Contact>>
      id={'profile_edit_form'}
      initialValues={contact}
      onSubmit={submitForm}
      serverErrors={validationErrors}
      validationSchema={schema}
      validateOnChange
      enableReinitialize
      resetServerErrors={resetServerErrors}
    >
      <ProfileEditView fields={getRequired(schema)} />
    </FormikRedux>
  );
};

export const ProfileEditView: FC<{
  fields: PartialRecord<keyof Contact, { required: boolean }>;
}> = ({ fields }) => {
  const { t } = useTranslate('profile');
  const navigate = useNavigate();
  const { isSubmitting } = useFormikContext<Nullable<Contact>>();

  const options = useOptions();
  return (
    <WhiteBox>
      <Grid
        container
        mb={{ md: 32, xs: 32 }}
        columnSpacing={36}
        rowSpacing={{ xs: 21 }}
      >
        <Grid xs={12} sm={6} md={4.5}>
          <InputControlF
            label={t('FIRST_NAME')}
            name={'firstName'}
            type="text"
            placeholder={t('ENTER_FIRST_NAME')}
            max={50}
            optional={!fields.firstName?.required}
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit + '_input-control-first-name'
            )}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4.5}>
          <InputControlF
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit +
                '_input-control-middle-name'
            )}
            label={t('MIDDLE_NAME')}
            name={'middleName'}
            placeholder={t('ENTER_MIDDLE_NAME')}
            max={50}
            optional={!fields.middleName?.required}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4.5}>
          <InputControlF
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit + '_input-control-last-name'
            )}
            label={t('LAST_NAME')}
            name={'lastName'}
            max={50}
            placeholder={t('ENTER_LAST_NAME')}
            optional={!fields.lastName?.required}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4.5}>
          <DateControlF
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit + '_date-control-dob'
            )}
            label={t('DATE_OF_BIRTH')}
            name={'birthDate'}
            disableFutureDates
            optional={!fields.birthDate?.required}
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid container mb={20} columnSpacing={36} rowSpacing={{ xs: 21 }}>
        <Grid xs={12} sm={6} md={4.5}>
          <SelectControlF<BirthSex>
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit + '_select-control-birth-sex'
            )}
            label={t('BIRTH_SEX')}
            name={'birthSex'}
            placeholder={EMPTY}
            /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
            renderValue={(v) => (v ? t(BIRTH_SEX_ENUM_MAP[v]) : EMPTY)}
            optional={!fields.birthSex?.required}
          >
            {options.birthSex.map(({ value, label }, key) => {
              return (
                <SelectOption
                  data-test-id={testId(
                    DATA_TEST_IDS.profileEditProfileEdit +
                      '_select-control-option-bs' +
                      key.toString()
                  )}
                  value={value}
                  key={key}
                >
                  {label}
                </SelectOption>
              );
            })}
          </SelectControlF>
        </Grid>
        <Grid xs={12} sm={6} md={4.5}>
          <SelectControlF<ContactGenderEnum>
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit + '_select-control-gender'
            )}
            label={t('GENDER')}
            placeholder={EMPTY}
            name={'gender'}
            /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
            renderValue={(v) => (v ? t(GENDER_ENUM_MAP[v]) : EMPTY)}
            optional={!fields.gender?.required}
          >
            {options.gender.map(({ value, label }, key) => {
              return (
                <SelectOption
                  data-test-id={testId(
                    DATA_TEST_IDS.profileEditProfileEdit +
                      '_select-control-option-gender' +
                      key.toString()
                  )}
                  value={value}
                  key={key}
                >
                  {label}
                </SelectOption>
              );
            })}
          </SelectControlF>
        </Grid>
        <Grid xs={12} sm={6} md={4.5}>
          <SelectControlF<ContactSexualOrientationEnum>
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit +
                '_select-control-sex-orient'
            )}
            label={t('SEXUAL_ORIENTATION')}
            placeholder={EMPTY}
            name={'sexualOrientation'}
            optional={!fields.sexualOrientation?.required}
            /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
            renderValue={(v) => (v ? t(SEXUAL_ORIENTATION_ENUM_MAP[v]) : EMPTY)}
          >
            {options.sexualOrientation.map(({ value, label }, key) => {
              return (
                <SelectOption
                  data-test-id={testId(
                    DATA_TEST_IDS.profileEditProfileEdit +
                      '_select-control-option-sex-orient' +
                      key.toString()
                  )}
                  value={value}
                  key={key}
                >
                  {label}
                </SelectOption>
              );
            })}
          </SelectControlF>
        </Grid>
        <Grid xs={12} sm={6} md={4.5}>
          <SelectControlF<ContactRaceEnum>
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit + '_select-control-race'
            )}
            label={t('RACE')}
            placeholder={EMPTY}
            name={'race'}
            optional={!fields.race?.required}
            /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
            renderValue={(v) => (v ? t(RACE_ENUM_MAP[v]) : EMPTY)}
          >
            {options.race.map(({ value, label }, key) => {
              return (
                <SelectOption
                  data-test-id={testId(
                    DATA_TEST_IDS.profileEditProfileEdit +
                      '_select-control-option-race' +
                      key.toString()
                  )}
                  value={value}
                  key={key}
                >
                  {label}
                </SelectOption>
              );
            })}
          </SelectControlF>
        </Grid>
        <Grid xs={12} sm={6} md={4.5}>
          <SelectControlF<ContactEthnicityEnum>
            data-test-id={testId(
              DATA_TEST_IDS.profileEditProfileEdit + '_select-control-ethnicity'
            )}
            label={t('ETHNICITY')}
            placeholder={EMPTY}
            name={'ethnicity'}
            optional={!fields.ethnicity?.required}
            /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
            renderValue={(v) => (v ? t(ETHNICITY_ENUM_MAP[v]) : EMPTY)}
          >
            {options.ethnicity.map(({ value, label }, key) => {
              return (
                <SelectOption
                  data-test-id={testId(
                    DATA_TEST_IDS.profileEditProfileEdit +
                      '_select-option-eth' +
                      key.toString()
                  )}
                  value={value}
                  key={key}
                >
                  {label}
                </SelectOption>
              );
            })}
          </SelectControlF>
        </Grid>
      </Grid>
      <Stack sx={styles.buttonsLayout}>
        <Button
          data-test-id={testId(
            DATA_TEST_IDS.profileEditProfileEdit + '_button-cancel'
          )}
          color={'primary'}
          variant="outlined"
          onClick={() => {
            navigate({
              pathname: AppRoutes.PROFILE,
              search: window.location.search,
            });
          }}
        >
          {t('CANCEL')}
        </Button>
        <Button
          data-test-id={testId(
            DATA_TEST_IDS.profileEditProfileEdit + '_button-save'
          )}
          color="primary"
          disabled={isSubmitting}
          type={'submit'}
          form="profile_edit_form"
        >
          {t('SAVE')}
        </Button>
      </Stack>
    </WhiteBox>
  );
};
